// web/src/components/CoverHero.tsx
// Magazine-cover-style hero. The opening shot of MatchDetail (and the
// featured MatchCard variant uses the same compositional rules).
//
// Layered (top → bottom):
//   - Masthead   (small, locked top-left)
//   - SubDeck    (cover-line strip top-right)
//   - Stickers   (tilted, scattered)
//   - CoverLine  (declarative headline, bottom-third)
//   - Subtitle   (small caps under cover line)
//
// Photo path (if `photoUrl`): full-bleed image, dark bottom-up gradient,
// content overlaid in cream.
// Flat-swatch fallback (no photo): saturated colour swatch driven by
// `resultSwatch`, with a giant squad-number-or-glyph treatment for visual
// interest.

import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { CoverLine } from './CoverLine';
import { SubDeck } from './SubDeck';

export interface CoverHeroProps {
  /** Lazy photo URL (signed). Renders as full-bleed hero if present. */
  photoUrl?: string | null;
  /** Headline text — usually from coverLineFor(match). */
  coverLine: string;
  /** Smaller subtitle below the cover line (kid name + position). */
  subtitle?: string;
  /** Masthead issue / context string, e.g. "ISSUE NO. 12 · MAY 26". */
  issue?: string;
  /** Top-right teaser strip items. */
  subDeck?: string[];
  /** Stickers to scatter on the hero (MoM, Captain, milestones). */
  stickers?: ReactNode[];
  /**
   * Optional diagonal corner banner ("WIN!", "ROUT!", "CUP TIE"). Renders
   * absolutely-positioned over the hero — caller passes a <Sash /> element.
   */
  sash?: ReactNode;
  /**
   * Background swatch when no photo. Maps to CSS var (without var() wrapper),
   * e.g. '--result-win'.
   */
  swatchVar?: string;
  /** Big numerical accent rendered in the no-photo fallback. */
  fallbackGlyph?: string | number;
  /**
   * When true (default), the cover-line types in like a magazine headline
   * on mount. When false, the headline just renders — used when MatchDetail
   * is reached via the post-save reveal (the score moment already happened
   * there, the cover doesn't need to re-animate).
   */
  animateEntrance?: boolean;
}

export function CoverHero({
  photoUrl,
  coverLine,
  subtitle,
  issue,
  subDeck,
  stickers,
  sash,
  swatchVar = '--green',
  fallbackGlyph,
  animateEntrance = true,
}: CoverHeroProps) {
  const hasPhoto = !!photoUrl;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      style={{
        position: 'relative',
        width: '100%',
        minHeight: 'clamp(360px, 56vw, 560px)',
        overflow: 'hidden',
        background: hasPhoto ? '#000' : `var(${swatchVar})`,
        color: 'var(--ink-on-green)',
      }}
    >
      {/* Photo layer */}
      {hasPhoto ? (
        <img
          src={photoUrl}
          alt=""
          loading="eager"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      ) : null}

      {/* Fallback glyph in the no-photo swatch — giant translucent numeral */}
      {!hasPhoto && fallbackGlyph !== undefined ? (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            right: '-2vw',
            bottom: '-4vw',
            fontFamily: 'var(--font-masthead)',
            fontStyle: 'italic',
            fontSize: 'clamp(12rem, 30vw, 24rem)',
            lineHeight: 0.85,
            color: 'rgba(255,255,255,0.13)',
          }}
        >
          {fallbackGlyph}
        </div>
      ) : null}

      {/* Dark gradient overlay (only on photo) — beefed up 2026-05-26
          after a parent reported the masthead + cover-line were
          unreadable over a bright daytime photo. Top scrim covers the
          masthead band (~25% of the hero); bottom scrim is heavier
          where the cover-line + subtitle live (~55% upward from base). */}
      {hasPhoto ? (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.25) 22%, rgba(0,0,0,0.1) 45%, rgba(0,0,0,0.65) 70%, rgba(0,0,0,0.9) 100%)',
          }}
        />
      ) : null}

      {/* Sash — sits above the content layers but inside the clipped hero */}
      {sash}

      {/* Content layers */}
      <div
        style={{
          position: 'relative',
          height: '100%',
          minHeight: 'inherit',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: 'var(--space-lg)',
        }}
      >
        {/* Top — masthead + sub-deck */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 'var(--space-base)',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-masthead)',
              fontStyle: 'italic',
              fontSize: '1.5rem',
              letterSpacing: '-0.01em',
              textTransform: 'uppercase',
              color: 'var(--ink-on-green)',
              textShadow: hasPhoto ? '0 1px 6px rgba(0,0,0,0.65)' : undefined,
              // Reserve right-side room for the sash so the masthead
              // doesn't run under the WIN! badge (2026-05-26).
              maxWidth: sash ? 'calc(100% - 110px)' : undefined,
            }}
          >
            MyFootballJournal
            {issue ? (
              <div
                style={{
                  marginTop: 4,
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--micro)',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontStyle: 'normal',
                  opacity: 0.75,
                }}
              >
                {issue}
              </div>
            ) : null}
          </div>

          {subDeck && subDeck.length > 0 ? (
            <SubDeck items={subDeck} onDark prefix="" />
          ) : null}
        </div>

        {/* Middle — stickers float */}
        {stickers && stickers.length > 0 ? (
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-base)',
              flexWrap: 'wrap',
              alignSelf: 'center',
              justifyContent: 'center',
              padding: 'var(--space-base)',
            }}
          >
            {stickers}
          </div>
        ) : null}

        {/* Bottom — cover line + subtitle. Slides up on first mount when
            animateEntrance is true (deep-link / browser back / direct
            navigation). When MatchDetail arrives from the post-save reveal
            screen, animateEntrance is false and the headline just appears
            — the score moment already happened on the reveal. */}
        <motion.div
          initial={animateEntrance ? { y: 32, opacity: 0 } : false}
          animate={animateEntrance ? { y: 0, opacity: 1 } : undefined}
          transition={{ duration: 0.55, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          style={
            // On a photo, sit the cover-line + subtitle on a subtle
            // dark-frosted panel so they're always readable regardless
            // of what's in the photo behind. Kept transparent enough
            // that the image still reads through; the gold accent on
            // the left edge keeps the magazine feel. 2026-05-26.
            hasPhoto
              ? {
                  position: 'relative',
                  background:
                    'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.5) 30%, rgba(0,0,0,0.65) 100%)',
                  WebkitBackdropFilter: 'blur(2px)',
                  backdropFilter: 'blur(2px)',
                  margin: 'calc(var(--space-lg) * -1)',
                  marginTop: 0,
                  padding: 'var(--space-lg)',
                  borderLeft: '3px solid var(--gold)',
                }
              : undefined
          }
        >
          <div
            style={
              // Drop-shadow on the headline as a defence-in-depth: even
              // if the scrim is suppressed (very tall photo, OS-level
              // accessibility setting, screenshot), the headline stays
              // legible.
              hasPhoto
                ? { textShadow: '0 2px 8px rgba(0,0,0,0.6)' }
                : undefined
            }
          >
            <CoverLine text={coverLine} size="display" align="left" />
          </div>
          {subtitle ? (
            <div
              style={{
                marginTop: 'var(--space-sm)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--small)',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: 'rgba(250,245,236,0.9)',
                textShadow: hasPhoto ? '0 1px 4px rgba(0,0,0,0.6)' : undefined,
              }}
            >
              {subtitle}
            </div>
          ) : null}
        </motion.div>
      </div>
    </motion.div>
  );
}
